import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Leaf, LayoutDashboard, LogOut, ShieldCheck, Briefcase, Camera } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user || user.role === "tech" || user.role === "admin") { setUnread(0); return; }
    const loadUnread = () => api.get("/notifications/summary").then(({ data }) => setUnread(data.total || 0)).catch(() => {});
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    const onFocus = () => loadUnread();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goPricing = () => {
    navigate("/#pricing");
    setTimeout(() => {
      document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <header
      className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-black/5"
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="h-9 w-9 rounded-xl bg-[#114A31] flex items-center justify-center">
            <Leaf className="h-5 w-5 text-white" />
          </span>
          <span className="font-heading text-lg font-semibold tracking-tight text-[#1C1C1C]">
            ScorpPath
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8 font-body text-sm text-[#595D5A]">
          <button onClick={goPricing} className="hover:text-[#114A31] transition-colors" data-testid="nav-pricing">
            Pricing
          </button>
          <Link to="/guide" className="hover:text-[#114A31] transition-colors" data-testid="nav-guide">
            Conversion Guide
          </Link>
        </div>

        <div className="flex items-center gap-1 sm:gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin")}
                  className="rounded-full text-[#114A31] hover:bg-[#E6F0EB]"
                  data-testid="nav-admin-btn"
                >
                  <ShieldCheck className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Admin</span>
                </Button>
              )}
              {user.role === "tech" && (
                <Button
                  variant="ghost"
                  onClick={() => navigate("/tech")}
                  className="rounded-full text-[#114A31] hover:bg-[#E6F0EB]"
                  data-testid="nav-tech-btn"
                >
                  <Briefcase className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Work Queue</span>
                </Button>
              )}
              {user.role === "user" && (
                <Button variant="ghost" onClick={() => navigate("/vault")} data-testid="nav-vault-btn"
                  className="rounded-full text-[#114A31] hover:bg-[#E6F0EB]">
                  <Camera className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Vault</span>
                </Button>
              )}
              {user.role !== "tech" && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/dashboard")}
                    className="rounded-full text-[#114A31] hover:bg-[#E6F0EB]"
                    data-testid="nav-dashboard-btn"
                  >
                    <LayoutDashboard className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Dashboard</span>
                  </Button>
                  {unread > 0 && (
                    <span data-testid="nav-unread-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#B91C1C] text-white text-[10px] font-semibold flex items-center justify-center shadow-sm">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </div>
              )}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="rounded-full text-[#595D5A] hover:bg-[#E6F0EB]"
                data-testid="nav-logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="rounded-full text-[#595D5A] hover:text-[#114A31] hover:bg-[#E6F0EB]"
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                onClick={() => navigate("/register")}
                className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white px-6"
                data-testid="nav-signup-btn"
              >
                Get Started
              </Button>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}
